import {
  doc,
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  addDoc,
  writeBatch,
  where,
  QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  getDoc,
  updateDoc,
  increment,
  Unsubscribe,
  Timestamp,
} from "firebase/firestore";
import { db } from "./firebase";
import { Conversation } from "../@types/message";
import { Message } from "../@types/message";
import { resolveClientDisplayName } from "../_helpers/string";

const MESSAGES_PER_PAGE = 25;

export const getConversations = async (
  userId: string
): Promise<Conversation[]> => {
  const conversationsRef = collection(db, "users", userId, "messages");
  const q = query(conversationsRef, orderBy("lastMessageAt", "desc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ ...doc.data() } as Conversation));
};

export const subscribeToConversations = (
  userId: string,
  callback: (conversations: Conversation[]) => void
): Unsubscribe => {
  const conversationsRef = collection(db, "users", userId, "messages");
  const q = query(conversationsRef, orderBy("lastMessageAt", "desc"));
  return onSnapshot(q, (snapshot) => {
    const conversations = snapshot.docs.map((docSnap) => {
      const data = docSnap.data() as Partial<Conversation>;
      return {
        ...data,
        clientUid: data.clientUid || docSnap.id,
      } as Conversation;
    });
    callback(conversations);
  });
};

export const getMessages = async (
  userId: string,
  clientUid: string,
  pageSize: number = MESSAGES_PER_PAGE,
  lastDoc?: QueryDocumentSnapshot
): Promise<{ messages: Message[]; lastDoc: QueryDocumentSnapshot | null }> => {
  const threadRef = collection(
    db,
    "users",
    userId,
    "messages",
    clientUid,
    "thread"
  );
  const q = lastDoc
    ? query(threadRef, orderBy("createdAt", "desc"), limit(pageSize), startAfter(lastDoc))
    : query(threadRef, orderBy("createdAt", "desc"), limit(pageSize));
  const snapshot = await getDocs(q);
  const messages = snapshot.docs
    .map((doc) => ({ uid: doc.id, ...doc.data() } as Message))
    .reverse();
  const newLastDoc =
    snapshot.docs.length > 0
      ? snapshot.docs[snapshot.docs.length - 1]
      : null;
  return { messages, lastDoc: newLastDoc };
};

export const subscribeToThread = (
  userId: string,
  clientUid: string,
  messageLimit: number,
  callback: (messages: Message[]) => void
): Unsubscribe => {
  const threadRef = collection(
    db,
    "users",
    userId,
    "messages",
    clientUid,
    "thread"
  );
  // Listen to latest N messages (desc + limit, then reverse for display order)
  const q = query(
    threadRef,
    orderBy("createdAt", "desc"),
    limit(messageLimit)
  );
  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs
      .map((doc) => ({ uid: doc.id, ...doc.data() } as Message))
      .reverse();
    callback(messages);
  });
};

export const getOlderMessages = async (
  userId: string,
  clientUid: string,
  beforeMessage: Message,
  pageSize: number = MESSAGES_PER_PAGE
): Promise<Message[]> => {
  const threadRef = collection(
    db,
    "users",
    userId,
    "messages",
    clientUid,
    "thread"
  );
  const q = query(
    threadRef,
    orderBy("createdAt", "desc"),
    startAfter(beforeMessage.createdAt),
    limit(pageSize)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map((doc) => ({ uid: doc.id, ...doc.data() } as Message))
    .reverse();
};

export const sendMessage = async (
  userId: string,
  clientUid: string,
  clientName: string,
  text: string,
  senderName: string,
  sentBy: "client" | "team",
  imageUrl?: string
): Promise<void> => {
  const needsClientLookup =
    !clientName?.trim() || clientName.trim() === "Client";

  let resolvedClientName = clientName.trim();
  if (needsClientLookup) {
    const clientRef = doc(db, "users", userId, "clients", clientUid);
    const clientSnap = await getDoc(clientRef);
    resolvedClientName = clientSnap.exists()
      ? resolveClientDisplayName(clientSnap.data())
      : resolveClientDisplayName(null, clientName);
  }

  const threadRef = collection(
    db,
    "users",
    userId,
    "messages",
    clientUid,
    "thread"
  );
  const conversationRef = doc(db, "users", userId, "messages", clientUid);

  const messageData: Record<string, any> = {
    text,
    sentBy,
    senderName,
    createdAt: serverTimestamp(),
    read: false,
  };
  if (imageUrl) {
    messageData.imageUrl = imageUrl;
  }

  await addDoc(threadRef, messageData);

  const unreadField =
    sentBy === "client" ? "unreadCount" : "clientUnreadCount";

  await setDoc(
    conversationRef,
    {
      clientUid,
      clientName: resolvedClientName,
      lastMessage: text,
      lastMessageAt: serverTimestamp(),
      lastMessageBy: sentBy,
      [unreadField]: increment(1),
    },
    { merge: true }
  );
};

export const markMessagesAsRead = async (
  userId: string,
  clientUid: string,
  reader: "team" | "client"
): Promise<void> => {
  const threadRef = collection(
    db,
    "users",
    userId,
    "messages",
    clientUid,
    "thread"
  );
  const senderToMark = reader === "team" ? "client" : "team";
  const q = query(
    threadRef,
    where("sentBy", "==", senderToMark),
    where("read", "==", false)
  );
  const snapshot = await getDocs(q);

  if (snapshot.empty) return;

  const batch = writeBatch(db);
  const now = serverTimestamp();
  snapshot.docs.forEach((docSnap) => {
    batch.update(docSnap.ref, { read: true, readAt: now });
  });

  const conversationRef = doc(db, "users", userId, "messages", clientUid);
  const countField = reader === "team" ? "unreadCount" : "clientUnreadCount";
  batch.update(conversationRef, { [countField]: 0 });

  await batch.commit();
};

export const linkImageToJob = async (
  userId: string,
  clientUid: string,
  messageUid: string,
  jobUid: string,
  jobId: number,
  imageUrl: string,
  imageName: string,
  lineItemUid?: string,
  mountLabel?: string
): Promise<void> => {
  const jobRef = doc(db, "users", userId, "jobs", jobUid);
  const jobSnap = await getDoc(jobRef);
  if (!jobSnap.exists()) throw new Error("Job not found");

  const jobData = jobSnap.data();
  const newFile = {
    name: imageName,
    url: imageUrl,
    createdAt: Timestamp.now(),
  };

  if (lineItemUid) {
    // Add to specific line item
    const updatedLineItems = (jobData.lineItems || []).map((li: any) => {
      if (li.uid === lineItemUid) {
        return { ...li, files: [...(li.files || []), newFile] };
      }
      return li;
    });
    await updateDoc(jobRef, { lineItems: updatedLineItems });
  } else {
    // Add to job-level files
    const existingFiles = jobData.files || [];
    await updateDoc(jobRef, { files: [...existingFiles, newFile] });
  }

  const messageRef = doc(
    db,
    "users",
    userId,
    "messages",
    clientUid,
    "thread",
    messageUid
  );
  const linkedToJob: Record<string, any> = { jobUid, jobId };
  if (lineItemUid) linkedToJob.lineItemUid = lineItemUid;
  if (mountLabel) linkedToJob.mountLabel = mountLabel;
  await updateDoc(messageRef, { linkedToJob });
};
